import i18n from '@/plugins/vue-i18n';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';

import to from 'await-to-js';
import axios from 'axios';
import { differenceWith, get, map, size, keyBy } from 'lodash';

// Stores
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';

export const useToolDataStore = defineStore('toolData', () => {
  // States
  const loading = shallowRef(false);
  const productHierarchy = ref({});
  const stores = ref([]);
  const customerSegments = ref([]);
  const periodIds = ref([]);
  const periodDates = ref([]);

  // Actions

  /**
   * Gets all products from tooldata-products by query (product display key or product name).
   */
  async function getProductsByQuery({ query, existingProducts }) {
    loading.value = true;
    const [err, result] = await to(axios.get(`/api/tool-data/products/${query}`));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetProducts', { data: query }));
    }

    // filter out products that are already present in scope
    const availableProducts = differenceWith(
      result.data,
      existingProducts,
      (p, ep) => p.productKey === ep.productKey
    );
    return availableProducts;
  }

  /**
   * Gets all customer segments from tooldata-customer-segments.
   */
  async function getCustomerSegments() {
    loading.value = true;
    const [err, result] = await to(axios.get('/api/tool-data/customer-segments'));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetCustomerSegments'));
    }
    return result.data;
  }

  /**
   * Gets all stores from tooldata-stores.
   */
  async function getStoreDetails({ selectedAssortmentGroupKeys }) {
    loading.value = true;
    const [err, result] = await to(
      axios.post('/api/tool-data/stores', { assortmentGroupKeys: selectedAssortmentGroupKeys })
    );
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetStores', { selectedAssortmentGroupKeys }));
    }
    return result.data;
  }

  async function getStores() {
    loading.value = true;
    const [err, result] = await to(axios.get('/api/tool-data/stores-all'));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetStores'));
    }
    stores.value = result.data;
  }

  async function getProducts({ params = {} }) {
    loading.value = true;
    const [err, result] = await to(axios.post('/api/tool-data/products/', params));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(
        i18n.t('errors.toolData.unableToGetProducts', {
          data: params.productKeys || params.productKeyDisplays,
        })
      );
    }
    return result.data;
  }

  /**
   * Gets all products from tooldata-products by assortment group keys and
   * switchingStartDate / switchingEndDate to retrieve timespan history
   */
  async function getProductsByAssortmentGroup({
    selectedAssortmentGroupKeys,
    switchingStartDate,
    switchingEndDate,
  }) {
    loading.value = true;
    const [err, result] = await to(
      axios.post('/api/tool-data/products/assortment-groups', {
        assortmentGroupKeys: selectedAssortmentGroupKeys,
        switchingStartDate,
        switchingEndDate,
      })
    );
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(
        i18n.t('errors.toolData.unableToGetProducts', { data: selectedAssortmentGroupKeys })
      );
    }
    return result.data;
  }

  /**
   * Gets all product rows and sub groups from tooldata-rom-products by category.
   */
  async function getROMProductDataByCategory({ categoryKey, existingProducts = [] }) {
    loading.value = true;
    const [err, romData] = await to(
      axios.post('/api/tool-data/products/rest-of-market', { categoryKey, existingProducts })
    );
    loading.value = false;
    if (err || get(romData, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetROMProducts', { categoryKey }));
    }
    return {
      rowData: get(romData, 'data.rowData', []),
      subGroups: get(romData, 'data.subGroups', {}),
    };
  }

  async function getPeriodIds() {
    loading.value = true;
    const [err, result] = await to(axios.get('/api/tool-data/product-transfer-price-periodIds'));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetCustomerSegments'));
    }

    periodIds.value = map(result.data, '_id');
    periodDates.value = keyBy(result.data, '_id');
  }

  /**
   * Gets all product attributes by selected attribute source from tooldata-attributes.
   */
  async function getProductAttributesBySource({ attributeSource, productKeys }) {
    loading.value = true;
    const [err, result] = await to(
      axios.post('/api/tool-data/product-attributes', { attributeSource, productKeys })
    );
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(
        i18n.t('errors.toolData.unableToGetAttributes', { attributeSource, productKeys })
      );
    }
    return result.data;
  }

  /**
   * Gets all product attributes' sources from tooldata-attributes.
   */
  async function getProductAttributeSources() {
    loading.value = true;
    const [err, result] = await to(axios.get('/api/tool-data/product-attributes/sources'));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetAttributesSources'));
    }
    return result.data;
  }

  /**
   * Gets lowest product hierarchy for selected category keys
   */
  async function fetchProductCategories({ pick, categoryKeys = [] }) {
    const workpackageProductsStore = useWorkpackageProductsStore();
    loading.value = true;
    const productCategoryKeys = size(categoryKeys)
      ? categoryKeys
      : map(workpackageProductsStore.workpackageProducts, 'productCategoryKey');
    if (!size(productCategoryKeys)) return; // nothing to do here
    const [err, result] = await to(
      axios.post('/api/tool-data/products-categories', { productCategoryKeys, pick })
    );
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetProductsHierarchy'));
    }
    productHierarchy.value = result.data;
  }

  /**
   * Fetches the relative performance metrics for a single product
   * from tooldata-product-metrics by the metric type
   */
  async function fetchRelativeProductPerformanceMetrics({ productKey, metricType }) {
    const params = { productKey, metricType };

    loading.value = true;
    const [err, result] = await to(
      axios.get('/api/tool-data/products/relative-performance', { params })
    );
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetProductMetrics', { data: productKey }));
    }
    return result.data;
  }

  /**
   * Gets category data for Rest of Market products
   */
  async function fetchROMCategories() {
    loading.value = true;
    const [err, result] = await to(axios.get('/api/tool-data/products/rest-of-market/categories'));
    loading.value = false;
    if (err || get(result, 'data.errors')) {
      throw new Error(i18n.t('errors.toolData.unableToGetROMCategories'));
    }
    return result.data;
  }

  return {
    // States
    loading,
    productHierarchy,
    stores,
    customerSegments,
    periodIds,
    periodDates,
    // Actions
    getProductsByQuery,
    getCustomerSegments,
    getStoreDetails,
    getStores,
    getProducts,
    getProductsByAssortmentGroup,
    getROMProductDataByCategory,
    getPeriodIds,
    getProductAttributesBySource,
    getProductAttributeSources,
    fetchProductCategories,
    fetchRelativeProductPerformanceMetrics,
    fetchROMCategories,
  };
});
